import React, { JSX, useState } from 'react';
import { ListRowItem, SimpleListProps } from './types';
import useTranslation from '../../Hooks/useTranslation';
import fallback from '../../../_Translations/generated/simple-list.en.t.json';
import { Button } from 'reactstrap';
import Keys from '../../../_Translations/generated/simple-list.en.t.json.keys';
import { listSegmentation } from './additionals';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { Element as ScrollTo } from 'react-scroll';
import ScrollerService from '../../../ISO/Services/ScrollerService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';

type TableBodyProps<L extends ListRowItem> = {
  listRows: SimpleListProps<L>['listRows'];
  itemsInRow?: number;
  isDesktop?: boolean;
  className?: string;
  onSelect: (id: string, code: string, index?: number) => void;
  checkAvailability?: (id: string, code: string, index?: number) => Promise<boolean>;
  customRowError?: () => JSX.Element;
  errorRowColSpanDiff?: number;
};

export const TableBody = <L extends ListRowItem>(props: TableBodyProps<L>): JSX.Element => {
  const { listRows, className, errorRowColSpanDiff } = props;
  return (
    <TransitionGroup
      component="tbody"
      className={`simple-list__transition${!!className ? ` ${className}` : ''}`}
    >
      {listRows.map((listRow, index) => (
        <CSSTransition key={listRow.id} nodeRef={listRow.nodeRef} timeout={400} classNames="fade">
          <ListRow
            {...props}
            listRow={listRow}
            errorRowColSpanDiff={errorRowColSpanDiff}
            key={listRow.id}
            index={index}
          />
        </CSSTransition>
      ))}
    </TransitionGroup>
  );
};

const ListRow = <L extends ListRowItem>(
  props: TableBodyProps<L> & { listRow: TableBodyProps<L>['listRows'][0]; index: number }
) => {
  const { listRow, listRows, isDesktop, itemsInRow, errorRowColSpanDiff } = props;
  const { Error, isAvailable, onSelect, t, isLoading } = useTableRowHook(props);

  return (
    <>
      <tr className={listRow.specialRow ? `simple-list__body__special ${listRow.groupSize}` : ''}>
        {listSegmentation(listRow.item, itemsInRow, isDesktop).map(item => (
          <td key={item.id} className={item.className || ''}>
            {Object.keys(item.list).map(key =>
              !!item.list[key].imgSrc ? (
                <div key={key}>
                  <img src={item.list[key].imgSrc} alt="image" />
                </div>
              ) : (
                <div
                  key={key}
                  className={`simple-list__body__item ${item.list[key].className || ''}`}
                >
                  {item.list[key].value}
                </div>
              )
            )}
          </td>
        ))}
        <td key="button">
          <Button
            color="primary"
            disabled={isLoading}
            onClick={(): Promise<void> =>
              onSelect(listRow.id, listRow.code, listRows.indexOf(listRow))
            }
          >
            {t(Keys.select)}
          </Button>
        </td>
      </tr>

      {!isAvailable ? (
        <tr className="simple-list__warning">
          <td colSpan={Object.keys(listRow.item).length + (errorRowColSpanDiff || 0) || 5}>
            <Error />
          </td>
        </tr>
      ) : null}
    </>
  );
};

export const TableBodySmallest = <L extends ListRowItem>(props: {
  listRows: SimpleListProps<L>['listRows'];
  onSelect: (id: string, code: string, index?: number) => void;
  className?: string;
  checkAvailability?: (id: string, code: string, index?: number) => Promise<boolean>;
}): JSX.Element => {
  const { listRows, className } = props;
  return (
    <TransitionGroup
      component="tbody"
      className={`simple-list__transition${!!className ? ` ${className}` : ''}`}
    >
      {listRows.map((listRow, index) => (
        <CSSTransition key={listRow.id} nodeRef={listRow.nodeRef} timeout={400} classNames="fade">
          <ListRowSmallest {...props} listRow={listRow} key={listRow.id} index={index} />
        </CSSTransition>
      ))}
    </TransitionGroup>
  );
};

const ListRowSmallest = <L extends ListRowItem>(
  props: TableBodyProps<L> & { listRow: TableBodyProps<L>['listRows'][0]; index: number }
) => {
  const { listRow, listRows } = props;
  const { Error, isAvailable, onSelect, t, isLoading } = useTableRowHook(props);

  return (
    <>
      <tr className={listRow.specialRow ? `simple-list__body__special ${listRow.groupSize}` : ''}>
        <td className={'simple-list__body__container'}>
          {Object.keys(listRow.item)
            .filter(key => !listRow.item[key].hideInMobile)
            .map(key =>
              !!listRow.item[key].imgSrc ? (
                <div key={key} className={`${listRow.item[key].className || ''}`}>
                  <img src={listRow.item[key].imgSrc} alt="image" />
                </div>
              ) : (
                <div
                  key={key}
                  className={`simple-list__body__item ${listRow.item[key].className || ''}`}
                >
                  {listRow.item[key].value}
                </div>
              )
            )}
          <div key="button">
            <Button
              disabled={isLoading}
              color="primary"
              onClick={(): Promise<void> =>
                onSelect(listRow.id, listRow.code, listRows.indexOf(listRow))
              }
            >
              {t(Keys.select)}
            </Button>
          </div>
        </td>
      </tr>

      {!isAvailable ? (
        <tr className="simple-list__warning">
          <td>
            <Error />
          </td>
        </tr>
      ) : null}
    </>
  );
};

const DefaultError = ({ scrollName }: { scrollName: string }) => {
  const { t } = useTranslation('simple-list', fallback, Keys);
  return (
    <ScrollTo name={scrollName}>
      <div className="info-name-badge mb-4">
        <FontAwesomeIcon icon={faInfoCircle} />
        <div>{t(Keys.unAvailableListItem)}</div>
      </div>
    </ScrollTo>
  );
};

const useTableRowHook = <L extends ListRowItem>(props: TableBodyProps<L> & { index: number }) => {
  const { customRowError, index } = props;
  const [isAvailable, setIsAvailable] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const SCROLL_ELEMENT_ERROR = 'list-item-unavailable' + index;

  const { t } = useTranslation('simple-list', fallback, Keys);

  const onSelect = async (id: string, code: string, indx?: number) => {
    if (props.checkAvailability) {
      setIsAvailable(true);
      setIsLoading(true);
      if (!(await props.checkAvailability(id, code, indx))) {
        ScrollerService.scrollTo(SCROLL_ELEMENT_ERROR);
        setIsAvailable(false);
        setIsLoading(false);
        return;
      }
      setIsLoading(false);
    }
    props.onSelect(id, code, index);
  };
  const ErrorDef = () => <DefaultError scrollName={SCROLL_ELEMENT_ERROR} />;
  const Error = customRowError || ErrorDef;

  return {
    onSelect,
    isAvailable,
    Error,
    t,
    isLoading
  };
};
